"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { cn } from "@/utilities/core/cn";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { visiblePages } from "./paginationWindow";
export interface LinkPaginationProps {
  currentPage: number;
  totalPages: number;
  /** Crawlable URL of a page. */
  getPageHref: (page: number) => string;
  /** Optional client-side navigation; the link's default navigation is prevented. */
  onNavigate?: (page: number) => void;
  maxVisiblePages?: number;
  className?: string;
}
const ITEM_CLASSES = "inline-flex min-w-9 h-9 items-center justify-center px-3 text-sm bg-card text-card-foreground border border-border rounded-md";
const ACTIVE_CLASSES = "bg-primary text-primary-foreground";
const DISABLED_CLASSES = "opacity-50 cursor-not-allowed";

/**
 * Pagination rendered as real anchors so crawlers can follow every page.
 * Styled like `CardPagination`, which stays in use for in-page tabs without URLs.
 */
export function LinkPagination(t0) {
  const $ = _c(42);
  const {
    currentPage,
    totalPages,
    getPageHref,
    onNavigate,
    maxVisiblePages: t1,
    className
  } = t0;
  const maxVisiblePages = t1 === undefined ? 5 : t1;
  const labels = useLabels();
  if (totalPages <= 1) {
    return null;
  }
  let t2;
  if ($[0] !== getPageHref || $[1] !== onNavigate) {
    t2 = (page, content, key, ariaLabel, t3) => {
      const active = t3 === undefined ? false : t3;
      return <Link key={key} href={getPageHref(page)} prefetch={false} aria-label={ariaLabel} aria-current={active ? "page" : undefined} className={cn(ITEM_CLASSES, "hover:opacity-80", active && ACTIVE_CLASSES)} onClick={onNavigate ? event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
          return;
        }
        event.preventDefault();
        onNavigate(page);
      } : undefined}>{content}</Link>;
    };
    $[0] = getPageHref;
    $[1] = onNavigate;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const renderLink = t2;
  let t3;
  if ($[3] !== renderLink) {
    t3 = (target, disabled, content_0, key_0, ariaLabel_0) => disabled ? <span key={key_0} aria-disabled="true" aria-label={ariaLabel_0} className={cn(ITEM_CLASSES, DISABLED_CLASSES)}>{content_0}</span> : renderLink(target, content_0, key_0, ariaLabel_0);
    $[3] = renderLink;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const renderEnd = t3;
  const atStart = currentPage <= 1;
  const atEnd = currentPage >= totalPages;
  const {
    first,
    previous,
    next,
    last,
    ariaLabel: ariaLabel_1
  } = labels.pagination;
  let t4;
  if ($[5] !== className) {
    t4 = cn("flex flex-wrap items-center justify-center gap-2", className);
    $[5] = className;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== atStart || $[8] !== first || $[9] !== renderEnd) {
    t5 = renderEnd(1, atStart, "\xAB", "first", first);
    $[7] = atStart;
    $[8] = first;
    $[9] = renderEnd;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = currentPage - 1;
  let t7;
  if ($[11] !== atStart || $[12] !== previous || $[13] !== renderEnd || $[14] !== t6) {
    t7 = renderEnd(t6, atStart, "\u2039", "previous", previous);
    $[11] = atStart;
    $[12] = previous;
    $[13] = renderEnd;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== currentPage || $[17] !== maxVisiblePages || $[18] !== renderLink || $[19] !== totalPages) {
    let t9;
    if ($[21] !== currentPage || $[22] !== renderLink) {
      t9 = page_0 => renderLink(page_0, page_0, `page-${page_0}`, undefined, page_0 === currentPage);
      $[21] = currentPage;
      $[22] = renderLink;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    t8 = visiblePages(currentPage, totalPages, maxVisiblePages).map(t9);
    $[16] = currentPage;
    $[17] = maxVisiblePages;
    $[18] = renderLink;
    $[19] = totalPages;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = currentPage + 1;
  let t10;
  if ($[24] !== atEnd || $[25] !== next || $[26] !== renderEnd || $[27] !== t9) {
    t10 = renderEnd(t9, atEnd, "\u203A", "next", next);
    $[24] = atEnd;
    $[25] = next;
    $[26] = renderEnd;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== atEnd || $[30] !== last || $[31] !== renderEnd || $[32] !== totalPages) {
    t11 = renderEnd(totalPages, atEnd, "\xBB", "last", last);
    $[29] = atEnd;
    $[30] = last;
    $[31] = renderEnd;
    $[32] = totalPages;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== ariaLabel_1 || $[35] !== t10 || $[36] !== t11 || $[37] !== t4 || $[38] !== t5 || $[39] !== t7 || $[40] !== t8) {
    t12 = <nav aria-label={ariaLabel_1} className={t4}>{t5}{t7}{t8}{t10}{t11}</nav>;
    $[34] = ariaLabel_1;
    $[35] = t10;
    $[36] = t11;
    $[37] = t4;
    $[38] = t5;
    $[39] = t7;
    $[40] = t8;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  return t12;
}